import type { SyntheticEvent } from 'react';

import { snakeCase } from 'lodash-es';

import { useI18n } from '@change-corgi/core/react/i18n';

import { useLocale } from 'src/app/shared/hooks/l10n';

type Variant = 'flyer-a' | 'flyer-b' | 'flyer-c';

export function useFlyerImages(variant: Variant) {
	const { translate } = useI18n();
	const locale = useLocale();

	const webp = {
		srcset: `https://static.change.org/flyer-templates/${variant}/${variant}-${locale}.webp 1x, https://static.change.org/flyer-templates/${variant}/${variant}-${locale}-2x.webp 2x`,
	};
	const png = {
		src: `https://static.change.org/flyer-templates/${variant}/${variant}-${locale}.png`,
		srcset: `https://static.change.org/flyer-templates/${variant}/${variant}-${locale}.png 1x, https://static.change.org/flyer-templates/${variant}/${variant}-${locale}-2x.png 2x`,
	};
	const fallback = {
		srcset: `https://static.change.org/flyer-templates/${variant}/${variant}.png 1x, https://static.change.org/flyer-templates/${variant}/${variant}-2x.png 2x`,
		src: `https://static.change.org/flyer-templates/${variant}/${variant}.png`,
	};

	return {
		webp,
		png,
		alt: translate(`corgi.petition.starter_dashboard.shared.flyer.${snakeCase(variant)}_alt_text`),
		// setting a fallback via the image's onerror in case the passed locale does not have a corresponding image
		onError: (event?: SyntheticEvent<HTMLImageElement, Event>) => {
			if (!event || !event.currentTarget.parentNode) return;
			if (event.currentTarget.getAttribute('data-error')) return;

			/* eslint-disable no-param-reassign */
			Array.from(event.currentTarget.parentNode.children).forEach((element) => {
				if (element instanceof HTMLImageElement) {
					element.srcset = fallback.srcset;
					element.src = fallback.src;
					// set data-error attribute so if the fallback fails we don't go into an infinite loop
					element.setAttribute('data-error', 'true');
				}
				if (element instanceof HTMLSourceElement) {
					element.srcset = fallback.srcset;
					element.type = 'image/png';
				}
			});
			/* eslint-enable no-param-reassign */
		},
	};
}
